import React, { Suspense, useMemo, useEffect, useState, useRef, lazy } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getProductUrlSegment } from '../utils/contactUrlHelper';

const NewContactModal = lazy(() => import('../components/modals/NewContactModal'));
import SpotlightSearch from '../components/SpotlightSearch';
import {
  Home,
  Users,
  Target,
  FileText,
  CheckCircle2,
  Calendar,
  MessageSquare,
  Heart,
  Zap,
  BarChart3,
  PieChart,
  Newspaper,
  Shield,
  Building2,
  DollarSign,
  Settings,
  Bell,
  Search,
  LogOut,
  Plus,
  Coins,
  ClipboardCheck,
  LayoutGrid,
  Briefcase,
  TrendingUp,
  Wrench
} from 'lucide-react';
import { httpClient } from '@alvo/api';
import { useAuthStore, useThemeStore, useUIStore, useContactsStore } from '@alvo/store';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  badge?: number;
  minLevel?: number;
}

interface NavSection {
  title: string;
  icon?: React.ElementType;
  items: NavItem[];
  minLevel?: number;
}

const navSections: (NavSection & { icon: React.ElementType })[] = [
  {
    title: 'Overview',
    icon: LayoutGrid,
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/dashboard', minLevel: 1 },
      { id: 'contacts', label: 'Contacts', icon: Users, path: '/contacts', minLevel: 1 },
      { id: 'pipeline', label: 'Pipeline', icon: Target, path: '/pipeline', minLevel: 1 },
      { id: 'policies', label: 'Policies', icon: FileText, path: '/policies', minLevel: 1 }
    ]
  },
  {
    title: 'Productivity',
    icon: Briefcase,
    items: [
      { id: 'tasks', label: 'Tasks', icon: CheckCircle2, path: '/tasks', minLevel: 1 },
      { id: 'calendar', label: 'Calendar', icon: Calendar, path: '/calendar', minLevel: 1 },
      { id: 'communications', label: 'Communications', icon: MessageSquare, path: '/communications', minLevel: 1 }
    ]
  },
  {
    title: 'Growth',
    icon: TrendingUp,
    minLevel: 3,
    items: [
      { id: 'retention', label: 'Retention', icon: Heart, path: '/retention', minLevel: 3 },
      { id: 'automations', label: 'Automations', icon: Zap, path: '/automations', minLevel: 3 },
      { id: 'precision', label: 'Sales Intel', icon: Target, path: '/sales-intel', minLevel: 3 },
      { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/analytics', minLevel: 3 },
      { id: 'reports', label: 'Reports', icon: PieChart, path: '/reports', minLevel: 4 }
    ]
  },
  {
    title: 'Operations',
    icon: Wrench,
    minLevel: 1,
    items: [
      { id: 'documents', label: 'Documents', icon: Newspaper, path: '/documents', minLevel: 1 },
      { id: 'licensing', label: 'Licensing', icon: Shield, path: '/licensing', minLevel: 1 },
      { id: 'compliance', label: 'Compliance', icon: Shield, path: '/compliance', minLevel: 3 },
      { id: 'accounting', label: 'Accounting', icon: DollarSign, path: '/accounting', minLevel: 4 },
      { id: 'commissions', label: 'Commissions', icon: Coins, path: '/commissions', minLevel: 4 },
      { id: 'contracting', label: 'Contracting', icon: ClipboardCheck, path: '/contracting', minLevel: 3 },
      { id: 'team', label: 'Team', icon: Building2, path: '/team', minLevel: 3 },
      { id: 'security', label: 'Security Settings', icon: Shield, path: '/security', minLevel: 3 },
      { id: 'settings', label: 'Settings', icon: Settings, path: '/settings', minLevel: 1 }
    ]
  }
];

const MainLayout: React.FC = () => {
  const location = useLocation();
  const previousLocationRef = useRef(location);

  // Detect /contacts/new by listening to actual browser navigation
  // (React Router location may be the background location in overlay pattern)
  const [isNewContactModal, setIsNewContactModal] = useState(window.location.pathname === '/contacts/new');
  
  useEffect(() => {
    const check = () => {
      const isModal = window.location.pathname === '/contacts/new';
      setIsNewContactModal(isModal);
      if (!isModal) {
        previousLocationRef.current = location;
      }
    };
    check();
    // Listen for both popstate and custom navigation events
    window.addEventListener('popstate', check);
    // Also poll briefly since pushState doesn't fire popstate
    const interval = setInterval(check, 100);
    return () => {
      window.removeEventListener('popstate', check);
      clearInterval(interval);
    };
  }, [location]);

  useEffect(() => {
    const savedToken = localStorage.getItem('alvo-token');
    if (savedToken) {
      httpClient.setAuthToken(savedToken);
    }
  }, []);

  const navigate = useNavigate();
  const { user, logout, isAgent } = useAuthStore();
  const { colors, mode, toggleMode } = useThemeStore();

  useEffect(() => {
    document.documentElement.style.setProperty('--app-bg', colors.bg);
    document.documentElement.style.setProperty('--app-text', colors.text);
  }, [colors.bg, colors.text]);
  
  const { globalSearch, setGlobalSearch, notificationCount } = useUIStore();
  const [searchOpen, setSearchOpen] = useState(false);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const {
    tasks: allTasks,
    contacts: allContacts,
    policies: allPolicies,
    setContacts,
    setPolicies,
    setTasks,
    addContact
  } = useContactsStore();

  const tasks = useMemo(() => {
    if (!isAgent() || !user) return allTasks;
    return allTasks.filter(t =>
      t.assignedTo === user.id ||
      t.createdBy === user.id
    );
  }, [allTasks, isAgent, user]);

  const contacts = useMemo(() => {
    if (!isAgent() || !user) return allContacts;
    return allContacts.filter(c =>
      c.agentId === user.id ||
      c.agentName === user.name ||
      c.createdBy === user.id ||
      c.assignedTo === user.id
    );
  }, [allContacts, isAgent, user]);

  const policies = useMemo(() => {
    if (!isAgent() || !user) return allPolicies;
    return allPolicies.filter(p =>
      p.agentId === user.id ||
      p.agent === user.id ||
      p.agentName === user.name
    );
  }, [allPolicies, isAgent, user]);

  const [dataFetched, setDataFetched] = useState(false);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!user || dataFetched) return;

      try {
        const token = localStorage.getItem('alvo-token');
        const hdrs: Record<string, string> = { 'Accept': 'application/json', 'Content-Type': 'application/json' };
        if (token) hdrs['Authorization'] = 'Bearer ' + token;
        const doFetch = async (endpoint: string) => {
          try {
            const r = await fetch('/api/v1' + endpoint, { method: 'GET', credentials: 'omit', headers: hdrs });
            if (!r.ok) return { data: [] };
            return await r.json();
          } catch { return { data: [] }; }
        };
        const [contactsResponse, policiesResponse, tasksResponse] = await Promise.all([
          doFetch('/contacts?per_page=10000'),
          doFetch('/policies?per_page=10000'),
          doFetch('/tasks')
        ]);

        setContacts(contactsResponse.data || []);
        setPolicies(policiesResponse.data || []);
        setTasks(tasksResponse.data || []);
        setDataFetched(true);
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };

    fetchAllData();
  }, [user, dataFetched, setContacts, setPolicies, setTasks]);

  useEffect(() => {
    const handler = () => {
      const token = localStorage.getItem('alvo-token');
      const hdrs: Record<string, string> = { 'Accept': 'application/json', 'Content-Type': 'application/json' };
      if (token) hdrs['Authorization'] = 'Bearer ' + token;
      fetch('/api/v1/policies?per_page=10000', { method: 'GET', credentials: 'omit', headers: hdrs })
        .then(r => r.ok ? r.json() : { data: [] })
        .then(res => setPolicies(res.data || []))
        .catch(() => {});
    };
    window.addEventListener('alvo:import-complete', handler);
    return () => window.removeEventListener('alvo:import-complete', handler);
  }, [setPolicies]);

  const currentPath = location.pathname;

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const isNavItemVisible = (item: NavItem): boolean => {
    // Capability-based access control: check user.capabilities first
    const caps = user?.capabilities;
    if (caps && item.id && caps[item.id]) {
      return caps[item.id] !== 'none';
    }
    // Fallback to role level
    const userLevel = user?.roleLevel || 1;
    const requiredLevel = item.minLevel || 1;
    return userLevel >= requiredLevel;
  };

  const pendingTasksCount = (() => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    return tasks.filter(t => {
      if ((t.status || 'PENDING').toUpperCase() === 'COMPLETED') return false;
      if (!t.dueDate) return true;
      return t.dueDate <= today;
    }).length;
  })();
  // Fetch real counts from API (not store arrays which are paginated)
  const [contactsCount, setContactsCount] = React.useState(0);
  const [activePoliciesCount, setActivePoliciesCount] = React.useState(0);
  React.useEffect(() => {
    const token = localStorage.getItem('alvo-token');
    if (!token) return;
    const headers = { 'Accept': 'application/json', 'Authorization': 'Bearer ' + token };
    fetch('/api/v1/contacts?per_page=1', { headers, credentials: 'omit' })
      .then(r => { if (!r.ok) throw new Error(r.status + ''); return r.json(); })
      .then(d => setContactsCount(d?.meta?.total || d?.total || 0))
      .catch(() => {});
    fetch('/api/v1/policies/badge-count', { headers, credentials: 'omit' })
      .then(r => { if (!r.ok) throw new Error(r.status + ''); return r.json(); })
      .then(d => setActivePoliciesCount(d?.active || 0))
      .catch(() => {});
  }, [user?.id]);

  // Check if we're on a contact detail page
  const isContactDetailPage = currentPath.startsWith('/contacts/') && currentPath !== '/contacts' && currentPath !== '/contacts/new';
  const isNewContactPage = currentPath === '/contacts/new';
  const showHeader = !isContactDetailPage && !isNewContactPage;

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        background: colors.bg,
        color: colors.text
      }}
    >
      {/* Dimming overlay */}
      {hoveredSection && (
        <div
          onClick={() => setHoveredSection(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: '220px',
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            zIndex: 90,
            transition: 'opacity 0.2s',
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        onMouseLeave={() => setHoveredSection(null)}
        style={{
          width: '220px',
          background: colors.cardBg,
          padding: '20px 0',
          display: 'flex',
          flexDirection: 'column',
          borderRight: `1px solid ${colors.border}`,
          flexShrink: 0,
          position: 'relative',
          zIndex: 100,
        }}
      >
        <div
          style={{
            padding: '8px 20px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <img
            src={mode === 'dark' ? '/alvo-logo-dark-blue.png' : '/alvo-logo-primary.png'}
            alt="Alvo"
            style={{ height: '50px', width: 'auto', display: 'block' }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
          <p style={{ fontSize: '11px', color: '#86868b', fontWeight: '400', marginTop: '4px', textAlign: 'center' }}>
            Sales, simplified.
          </p>
        </div>

        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', padding: '0 12px' }}>
          {navSections.map((section) => {
            const visibleItems = section.items.filter(isNavItemVisible);
            if (visibleItems.length === 0) return null;
            const SectionIcon = section.icon || LayoutGrid;
            const isHovered = hoveredSection === section.title;
            const hasActiveItem = visibleItems.some(item => currentPath === item.path);

            return (
              <div
                key={section.title}
                style={{ position: 'relative' }}
                onMouseEnter={() => setHoveredSection(section.title)}
              >
                <button
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    width: '100%',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: isHovered ? colors.text : hasActiveItem ? colors.primary : colors.textSecondary,
                    background: isHovered ? colors.secondaryBg : 'transparent',
                    textAlign: 'left',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <SectionIcon size={18} />
                  {section.title}
                </button>

                {/* Flyout panel */}
                {isHovered && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '100%',
                      top: 0,
                      marginLeft: '4px',
                      background: colors.cardBg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '14px',
                      padding: '8px',
                      minWidth: '220px',
                      boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      zIndex: 101,
                    }}
                  >
                    <div style={{ padding: '6px 12px 10px', fontSize: '11px', fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {section.title}
                    </div>
                    {visibleItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = currentPath === item.path;
                      const badge = item.id === 'tasks' ? pendingTasksCount
                              : item.id === 'contacts' ? contactsCount
                              : item.id === 'policies' ? activePoliciesCount
                              : item.badge;

                      return (
                        <button
                          key={item.id}
                          onClick={() => { handleNavClick(item.path); setHoveredSection(null); }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '9px 12px',
                            color: isActive ? '#ffffff' : colors.text,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            fontSize: '13px',
                            fontWeight: isActive ? 600 : 400,
                            borderRadius: '8px',
                            border: 'none',
                            background: isActive ? colors.primary : 'transparent',
                            width: '100%',
                            textAlign: 'left',
                          }}
                          onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = colors.secondaryBg; }}
                          onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        >
                          <Icon size={16} />
                          {item.label}
                          {badge > 0 && (
                            <span
                              style={{
                                marginLeft: 'auto',
                                background: colors.error,
                                color: '#fff',
                                fontSize: '10px',
                                padding: '1px 6px',
                                borderRadius: '10px',
                                fontWeight: 600,
                                minWidth: '18px',
                                textAlign: 'center' as const,
                              }}
                            >
                              {badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div
          style={{
            padding: '16px 12px',
            borderTop: `1px solid ${colors.border}`
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px',
              background: colors.secondaryBg,
              borderRadius: '10px'
            }}
          >
            <button
              onClick={() => navigate('/settings')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flex: 1,
                minWidth: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                textAlign: 'left'
              }}
            >
              <div
                style={{
                  width: '34px',
                  height: '34px',
                  background: colors.primary,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 600,
                  flexShrink: 0,
                  fontSize: '14px',
                }}
              >
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: colors.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {user?.name || 'User'}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: colors.textSecondary
                  }}
                >
                  {user?.roleName || 'Agent'}
                </div>
              </div>
            </button>
            <button
              onClick={handleLogout}
              title="Log out"
              style={{
                background: 'none',
                border: 'none',
                padding: '6px',
                borderRadius: '6px',
                color: colors.textSecondary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        {showHeader && (
          <header
            style={{
              padding: '16px 32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${colors.border}`,
              background: colors.cardBg,
              overflow: 'visible',
              zIndex: 100,
              position: 'relative' as const
            }}
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: colors.secondaryBg,
                padding: '10px 16px',
                borderRadius: '10px',
                width: '400px', position: 'relative' as const
              }}
            >
              <Search size={18} color={colors.textSecondary} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search contacts, policies..."
                value={globalSearch}
                onChange={(e) => { setGlobalSearch(e.target.value); if (e.target.value.length >= 2) setSearchOpen(true); else setSearchOpen(false); }}
                onFocus={() => { if (globalSearch.length >= 2) setSearchOpen(true); }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: colors.text,
                  fontSize: '14px',
                  width: '100%'
                }}
              />
              <kbd
                style={{
                  fontSize: '11px',
                  color: colors.textSecondary,
                  background: colors.tertiaryBg,
                  padding: '2px 6px',
                  borderRadius: '4px'
                }}
              >
                ⌘K
              </kbd>
              <SpotlightSearch
                query={globalSearch}
                onQueryChange={setGlobalSearch}
                isOpen={searchOpen}
                onClose={() => { setSearchOpen(false); setGlobalSearch(''); }}
                inputRef={searchInputRef}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => navigate('/contacts/new', { state: { backgroundLocation: location } })}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: colors.primary,
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                <Plus size={18} />
                Add Contact
              </button>

              <button
                onClick={toggleMode}
                style={{
                  background: colors.secondaryBg,
                  border: 'none',
                  padding: '10px',
                  borderRadius: '10px',
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {mode === 'dark' ? '☀️' : '🌙'}
              </button>

              <button
                onClick={() => navigate('/notifications')}
                style={{
                  background: colors.secondaryBg,
                  border: 'none',
                  padding: '10px',
                  borderRadius: '10px',
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <Bell size={18} />
                {notificationCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      width: '8px',
                      height: '8px',
                      background: colors.error,
                      borderRadius: '50%'
                    }}
                  />
                )}
              </button>
            </div>
          </header>
        )}

        {/* Page Content */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: showHeader ? '24px 32px' : 0
          }}
        >
          <Suspense fallback={
            <div style={{
              position: 'relative', width: '100%', height: '100%',
            }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                background: 'linear-gradient(90deg, transparent, #0071e3, transparent)',
                animation: 'loadingSlide 1.2s ease-in-out infinite',
              }} />
              <style>{`@keyframes loadingSlide { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }`}</style>
            </div>
          }>
            <Outlet />
          {isNewContactModal && (
            <Suspense fallback={null}>
              <NewContactModal
                isOpen={true}
                onClose={() => navigate(-1)}
                onSave={async (contactData) => {
                  const tempId = contactData.id || crypto.randomUUID();
                  await addContact({ ...contactData, id: tempId }, user);
                  // After addContact, the store may have replaced tempId with the backend ID
                  const stored = useContactsStore.getState().contacts.find(c => 
                    c.id === tempId || (c.firstName === contactData.firstName && c.lastName === contactData.lastName && c.phone === contactData.phone)
                  );
                  const realId = stored?.id || tempId;
                  navigate('/contacts/' + realId + '/' + getProductUrlSegment(contactData.product || ''));
                }}
              />
            </Suspense>
          )}
          </Suspense>
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
